/* eslint-disable no-use-before-define */
import { markRaw, toRaw } from 'vue';
import { produce, original } from 'immer';
import constants from '@/js/constants.js';
import JsonPatchSrv from '@/js/services/JsonPatchSrv';
import { innerHeight } from '@/components/Reusables/utils';
import { getDefaultTask, getDefaultMilestone, getDefaultMacro } from './helpers/defaultElements';
import helpers from './helpers/planningInitHelpers';
import useUsersAccessRightStore from '@/stores/users/accessRight';
import automationRulesEngine from '@/components/Automations/automationRulesEngine';

/** ************** */
/* ELEMENT MODEL */
/** ************** */
function defaultByType(type) {
  if (type == 'macro') return getDefaultMacro();
  return (type == 'milestone' ? getDefaultMilestone() : getDefaultTask());
}

class PlanningElement {
  constructor(planning, srcData) {
    let data = angular.copy(srcData) || {};

    if (data instanceof PlanningElement) {
      _.extend(this, data);
      data = this.data;
    } else {
      const defaultProps = defaultByType(data.type);
      _.extend(this, defaultProps);
      data = angular.merge({}, angular.copy(this.data), data);
      helpers.updateLastVersionsElements(data);
    }

    this.set(data);
    if (! this.id) this.id = 0;
    this.getPlanning = function () {
      return planning;
    };
  }

  clone() {
    return new PlanningElement(this.getPlanning(), this);
  }

  // Helper method to safely use produce without Vue reactivity interference
  updateData(updater) {
    const oldData = toRaw(this.data);
    this.data = markRaw(produce(oldData, updater));
    automationRulesEngine.onElementDataChanged({ element: this, oldData, newData: this.data });
  }

  getAll() {
    const nonNullData = { id: this.id };
    Object.keys(this.data).forEach((key) => { if (this.data[key] !== null && key !== 'id') nonNullData[key] = this.data[key]; });
    return nonNullData; // object is mutable but properties are frozen (from immer)
  }

  getData() {
    return this.data; // deeply unmutable
  }

  set(elData) {
    const data = elData || {};
    ['id', 'access_right'].forEach((attr) => {
      if (Object.prototype.hasOwnProperty.call(data, attr)) {
        this[attr] = data[attr];
        delete data[attr];
      }
    });
    this.updateData((draft) => {
      _.extend(draft, data);
    });
    return this;
  }

  reset({ id, ...data }) { // all data except id
    const defaultProps = defaultByType(data.type || this.type);
    this.updateData((draft) => {
      _.extend(draft, { ...defaultProps.data, ...data });
    });
    return this;
  }

  /** ********** */
  /* GET / SET */
  /** ********** */
  getType() {
    return this.data.type;
  }
  setType(value) {
    this.updateData((draft) => {
      draft.type = value;
    });
    return this;
  }
  isType(...args) {
    return args.includes(this.getType());
  }

  isVisibleOnPlanning() {
    return this.visible && ! this.filteredout && this.hasDates();
  }

  getLaneId() {
    return this.data.lane_id;
  }
  setLaneId(value) {
    this.updateData((draft) => {
      draft.lane_id = value;
    });
    return this;
  }

  getTitle() {
    return this.data.title;
  }
  setTitle(value) {
    this.updateData((draft) => {
      draft.title = value;
    });
    return this;
  }

  getTitleStyle() {
    return this.data.titleStyle;
  }
  setTitleStyle(value) {
    this.updateData((draft) => {
      draft.titleStyle = value;
    });
    return this;
  }

  getDescription() {
    return this.data.html;
  }
  setDescription(value) {
    this.updateData((draft) => {
      draft.html = value;
    });
    return this;
  }

  getDescriptionStyle() {
    return this.data.descriptionStyle;
  }
  setDescriptionStyle(value) {
    this.updateData((draft) => {
      draft.descriptionStyle = value;
    });
    return this;
  }

  getProcessStep() {
    return this.data.processStep || null;
  }
  setProcessStep(value) {
    this.updateData((draft) => {
      draft.processStep = value || ''; // do not set to null for change detection in getAll
    });
    return this;
  }

  getProcessPriority() {
    return this.data.processPriority || 0;
  }
  setProcessPriority(value) {
    this.updateData((draft) => {
      draft.processPriority = value || null;
    });
    return this;
  }

  hasDates() {
    return this.data.starttime && this.data.endtime && true || false;
  }
  getRawDates() {
    return { starttime: this.data.starttime, endtime: this.data.endtime };
  }

  getStartTime() {
    if (! this.data.starttime) return null;
    return moment(this.data.starttime);
  }
  setStartTime(value) {
    this.updateData((draft) => {
      if (moment.isMoment(value)) value = value.format();
      if (! value) value = ''; // do not set to null for change detection in getAll
      draft.starttime = value;
      if (this.isType('milestone')) draft.endtime = value;
    });
    return this;
  }

  getEndTime() {
    if (! this.data.endtime) return null;
    return moment(this.data.endtime);
  }
  setEndTime(value) {
    this.updateData((draft) => {
      if (moment.isMoment(value)) value = value.format();
      if (! value) value = ''; // do not set to null for change detection in getAll
      draft.endtime = value;
      if (this.isType('milestone')) draft.starttime = value;
    });
    return this;
  }

  getSchedule() {
    const schedule = this.data.schedule || { start: null, end: null };
    return {
      start: schedule.start && moment(schedule.start),
      end: schedule.end && moment(schedule.end),
    };
  }
  setSchedule(value) {
    this.updateData((draft) => {
      if (value?.start && moment.isMoment(value.start)) value.start = value.start.format();
      if (value?.end && moment.isMoment(value.end)) value.end = value.end.format();
      if (! value.start && ! value.end) value = { start: null, end: null };
      draft.schedule = value;
    });
    return this;
  }

  getRecurrenceId() {
    return this.data.recurrence_id;
  }
  setRecurrenceId(value) {
    this.updateData((draft) => {
      draft.recurrence_id = value;
    });
    return this;
  }

  getWidth() {
    return this.data.width;
  }
  setWidth(value) {
    this.updateData((draft) => {
      draft.width = value;
    });
    return this;
  }

  getPriority() {
    return this.data.priority;
  }
  setPriority(value) {
    this.updateData((draft) => {
      draft.priority = value;
    });
    return this;
  }

  getConfig(field) {
    if (field) return this.data.config?.[field];
    return this.data.config;
  }
  setConfig(field, value) {
    // setConfig({all}) or setConfig(field, value)
    this.updateData((draft) => {
      draft.config = draft.config || {};
      if (value !== undefined) {
        draft.config[field] = value;
      } else {
        draft.config = field;
      }
    });
    return this;
  }

  getProgress() {
    return this.data.progress;
  }
  setProgress(value) {
    this.updateData((draft) => {
      draft.progress = value;
    });
    return this;
  }

  getDependencies() {
    return this.data.dependencies;
  }
  setDependencies(value) {
    this.updateData((draft) => {
      draft.dependencies = value;
    });
    return this;
  }

  getSubTasks() {
    return this.data.subTasks;
  }
  setSubTasks(value) {
    this.updateData((draft) => {
      draft.subTasks = value?.length ? value : [];
    });
    return this;
  }

  getUsers() {
    return this.data.users;
  }
  setUsers(value) {
    this.updateData((draft) => {
      draft.users = value;
    });
    return this;
  }
  addUser(user) { // { (id, email, firstname, lastname, avatar), username, (group_id, title) } or username string
    if (! user) return this;
    this.updateData((draft) => {
      draft.users = draft.users || [];
      if (typeof user == 'object') { // user in company users or existing virtual participant
        if (! user.id && ! user.username && ! user.group_id) return;
        if (user.id && draft.users.find(item => item.id == user.id)) return;
        if (user.username && draft.users.find(item => item.username == user.username)) return;
        if (user.group_id && draft.users.find(item => item.group_id == user.group_id)) return;
        let fields = ['username'];
        if (user.id) fields = ['id', 'email', 'firstname', 'lastname', 'avatar'];
        if (user.group_id) fields = ['group_id', 'title'];
        draft.users.push({ ..._.pick(user, fields) });
      } else if (typeof user == 'string') { // virtual participant
        if (draft.users.find(item => item.username == user)) return;
        draft.users.push({ username: user });
      }
    });
    return this;
  }

  getChecklist() {
    return this.data.checklist;
  }
  setChecklist(value) {
    this.updateData((draft) => {
      draft.checklist = value;
    });
    return this;
  }
  setChecklistItem(item, changes) {
    this.updateData((draft) => {
      let index = original(draft.checklist)?.findIndex(i => i === item);
      if (index === -1) {
        console.error('Checklist item not found in element', this.id, item);
        index = draft.checklist?.findIndex(i => i.id && i.id == item.id);
      }
      if (index > -1) {
        _.extend(draft.checklist[index], changes);
      } else {
        console.error('Checklist item not found by id either');
      }
    });
    return this;
  }

  getLinks() {
    return this.data.links;
  }
  setLinks(value) {
    this.updateData((draft) => {
      draft.links = value;
    });
    return this;
  }
  setLinksItem(item, changes) {
    this.updateData((draft) => {
      const index = original(draft.links)?.findIndex(i => i === item);
      if (index > -1) {
        _.extend(draft.links[index], changes);
      } else {
        console.error('Link item not found in element', this.id, item);
      }
    });
    return this;
  }

  getBudgets() {
    return this.data.budgets;
  }
  setBudgets(value) {
    this.updateData((draft) => {
      draft.budgets = value;
    });
    return this;
  }
  setBudgetsItem(item, changes) {
    this.updateData((draft) => {
      const index = original(draft.budgets)?.findIndex(i => i === item);
      if (index > -1) {
        _.extend(draft.budgets[index], changes);
      } else {
        console.error('Budget item not found in element', this.id, item);
      }
    });
    return this;
  }

  getColorId() {
    return this.data.color;
  }
  setColorId(value) {
    this.updateData((draft) => {
      draft.color = (typeof value == 'number') ? value : 0;
    });
    return this;
  }
  getColorShadeId() {
    return this.data.colorShade;
  }
  setColorShadeId(value) {
    this.updateData((draft) => {
      draft.colorShade = (typeof value == 'number') ? value : '';
    });
    return this;
  }
  getIconId() {
    return this.data.icon;
  }
  setIconId(value) {
    this.updateData((draft) => {
      draft.icon = value;
    });
    return this;
  }

  getIsLocked() {
    return this.data.isLocked;
  }
  setIsLocked(value) {
    this.updateData((draft) => {
      draft.isLocked = value;
    });
    return this;
  }

  getTimeBar() {
    return this.data.timeBar || { colorId: '' };
  }
  setTimeBar(value) {
    this.updateData((draft) => {
      draft.timeBar = value;
    });
    return this;
  }

  getMeetingId() {
    return this.data.meeting_id;
  }
  setMeetingId(value) {
    this.updateData((draft) => {
      draft.meeting_id = value;
    });
    return this;
  }

  getCustomField(field) {
    if (field) return this.data.customFields?.[field];
    return this.data.customFields;
  }
  setCustomField(field, value) {
    this.updateData((draft) => {
      draft.customFields = draft.customFields || {};
      draft.customFields[field] = value;
    });
    return this;
  }

  /** ******** */
  /* DISPLAY */
  /** ******** */
  getSecondaryColor() {
    const { colors } = this.getPlanning().config;
    return colors[this.data.color]?.secondary || '#dedefd';
  }

  getColorClass() {
    return `el-color-${this.getColorId()}-${this.getColorShadeId()}`;
  }

  hasIcon() {
    return !! this.data.icon;
  }
  getIcon() {
    const { icons, removedIcons } = this.getPlanning().config;
    return icons[this.data.icon] || removedIcons[this.data.icon] || {};
  }

  getChecklistItemColor(item) {
    item = item || {};
    if (item.checked) return 'medium-emphasis';
    const deadline = item.duedate ? moment(item.duedate) : this.getEndTime();
    if (! deadline) return 'black';
    if (deadline.isBefore()) return 'errorred';
    if (deadline.add(-1, 'week').isBefore()) return 'warningorange';
    return 'black';
  }

  getChecklistItemClass(item) {
    return `text-${this.getChecklistItemColor(item)}`;
  }

  getEndTimeColorClass() { // for kanban mode
    if (this.getProgress() == 100) return '';
    if (this.getEndTime().isBefore()) return 'text-errorred';
    if (this.getEndTime().add(-1, 'week').isBefore()) return 'text-warningorange';
    return '';
  }

  updateXposition() {
    if (! this.hasDates()) return;
    const { visibleTimeline } = this.getPlanning();
    const starttime = convertToBusinessTimeEquivalent(this.getStartTime());
    let result = starttime.diffWithWorkdays(moment(visibleTimeline.starttime), 'minutes', visibleTimeline.workdays) / visibleTimeline.minuteperpx;

    if (this.isType('milestone')) {
      result -= this.getWidth() / 2;
    } else {
      result = Math.max(result, 0);
    }
    this.xposition = Math.round(result);
  }

  updateHeight() {
    if (! this.visible && ! this.hasDates()) { this.height = 0; return; }
    const height = innerHeight(document.getElementById(`el${this.id}`)); // with padding, not borders
    this.height = Math.round(height || this.height || 0);
  }

  updateWidth() {
    if (! this.hasDates()) return;
    const { visibleTimeline } = this.getPlanning();
    if (this.isType('milestone')) {
      this.visible = !! ((moment(visibleTimeline.starttime).isBefore(this.getStartTime()) && moment(visibleTimeline.endtime).isAfter(this.getStartTime())));
    } else {
      const elStarttime = convertToBusinessTimeEquivalent(this.getStartTime());
      const elEndtime = convertToBusinessTimeEquivalent(this.getEndTime());
      const starttime = moment.max(elStarttime, moment(visibleTimeline.starttime));
      const endtime = moment.min(elEndtime, moment(visibleTimeline.endtime).add(-1 * visibleTimeline.minuteperpx, 'minutes'));
      const duration = endtime.diffWithWorkdays(starttime, 'minutes', visibleTimeline.workdays);
      const realduration = elEndtime.diffWithWorkdays(elStarttime, 'minutes', visibleTimeline.workdays);
      const result = Math.max(constants.taskMinWidth, duration / visibleTimeline.minuteperpx - 1); // min resize
      if (result <= constants.taskMinWidth && duration < realduration) { this.visible = false; } else { this.visible = true; } // hide small overlaps at the edges
      this.setWidth(Math.round(result));
    }
  }

  update() {
    this.updateXposition();
    this.updateWidth();
  }

  /** ******** */
  /* ACTIONS */
  /** ******** */
  move(laneId, positionLeft) {
    if (! this.hasDates()) return;
    const { visibleTimeline } = this.getPlanning();
    this.setLaneId(laneId);
    // Update start time & end time
    if (this.isType('milestone')) positionLeft += this.getWidth() / 2;
    const elduration = this.getEndTime().diffWithWorkdays(this.getStartTime(), 'minutes', visibleTimeline.workdays);
    const starttime = moment(visibleTimeline.starttime).addWithWorkdays(positionLeft * visibleTimeline.minuteperpx, 'minutes', visibleTimeline.workdays);
    this.setStartTime(convertFromBusinessTimeEquivalent(starttime));
    this.setEndTime(this.getStartTime().addWithWorkdays(elduration, 'minutes', visibleTimeline.workdays));
    this.update(); // needed when xposition is programmaticaly set back to initial value
  }

  kanbanMove(laneId, referenceXPosition, referenceYPosition) { // el move in Kanban view / referenceXPosition may be right-click position or center of dragged element
    this.setLaneId(laneId);
    let colIndex = 0;
    const processSteps = this.getPlanning().process.steps;
    while (colIndex < processSteps.length && referenceXPosition >= processSteps[colIndex].width) {
      referenceXPosition -= processSteps[colIndex].width;
      colIndex++;
    }
    if (processSteps[colIndex]) this.setProcessStep(processSteps[colIndex].id);

    if (referenceYPosition) {
      // handle process priority
      const modifiedElements = [this];
      const sameCellElements = []; // visible elements only
      const hiddenSameCellElementsProcessPriority = []; // in backlog or xs columns elements may be hidden
      this.getPlanning().elements.forEach((el) => {
        if (el.isDragPlaceholder || el.id == this.id || el.getLaneId() != laneId || el.getProcessStep() != this.getProcessStep()) return;
        const $el = $(`#el${el.id}`);
        if ($el.length) {
          sameCellElements.push({ id: el.id, y: $el.offset().top + $el.height() / 2, el });
        } else {
          hiddenSameCellElementsProcessPriority.push(el.getProcessPriority());
        }
      });
      sameCellElements.sort((a, b) => a.y - b.y);

      if (sameCellElements.length) {
        let placeBetweenElement;
        for (let i = 0; i < sameCellElements.length; i++) {
          if (sameCellElements[i].y > referenceYPosition) {
            placeBetweenElement = { previous: sameCellElements[i - 1]?.el, next: sameCellElements[i].el, index: i };
            break;
          }
        }

        if (! placeBetweenElement) { // place last
          this.setProcessPriority(sameCellElements.at(-1).el.getProcessPriority() - 1024); // 1024 is arbitrary step value (easy to divide by 2)
        } else if (! placeBetweenElement.previous) { // place first
          this.setProcessPriority(placeBetweenElement.next.getProcessPriority() + 1024);
        } else if (Math.abs(placeBetweenElement.previous.getProcessPriority() - placeBetweenElement.next.getProcessPriority()) > 1e-3) { // place between when gap is > 0.001
          this.setProcessPriority((placeBetweenElement.previous.getProcessPriority() + placeBetweenElement.next.getProcessPriority()) / 2);
        } else { // no gap : reset all cell elements priority
          this.setProcessPriority(0);
          let priority = 1024;
          for (let i = placeBetweenElement.index - 1; i >= 0; i--) {
            sameCellElements[i].el.setProcessPriority(priority);
            priority += 1024;
          }
          priority = -1024;
          for (let i = placeBetweenElement.index; i < sameCellElements.length; i++) {
            sameCellElements[i].el.setProcessPriority(priority);
            priority -= 1024;
          }
          modifiedElements.push(...sameCellElements.map(item => item.el)); // nb : may include elements where process priority was not actually changed
        }
      } else if (hiddenSameCellElementsProcessPriority.length) {
        // no visible elements in cell (xs col), place on top
        this.setProcessPriority(Math.max(0, ...hiddenSameCellElementsProcessPriority) + 1024);
      }
      return modifiedElements; // for dropSelection to know if modifying 1 or all elements
    }
    return [this];
  }

  resize(elWidth, handle) {
    if (! this.hasDates()) return;
    const { visibleTimeline } = this.getPlanning();
    handle = handle || 'e';
    if (this.isType('macro', 'task')) {
      if (handle == 'e') {
        elWidth = Math.min(elWidth, visibleTimeline.pxwidth - this.xposition);
        const endtime = convertToBusinessTimeEquivalent(this.getStartTime()).addWithWorkdays(elWidth * visibleTimeline.minuteperpx, 'minutes', visibleTimeline.workdays);
        this.setEndTime(convertFromBusinessTimeEquivalent(endtime));
      }
      if (handle == 'w') {
        elWidth = Math.min(elWidth, this.xposition + this.getWidth());
        const starttime = convertToBusinessTimeEquivalent(this.getEndTime()).addWithWorkdays(-elWidth * visibleTimeline.minuteperpx, 'minutes', visibleTimeline.workdays);
        this.setStartTime(convertFromBusinessTimeEquivalent(starttime));
      }
    }
    this.setWidth(elWidth);
    this.update(); // update xposition
  }

  updateChecklistProgress() {
    let totalWeight = 0;
    let weight = 0;
    (this.getChecklist() || []).forEach((item) => {
      const itemWeight = item.weight === 0 ? 0 : (item.weight || 1);
      if (item.checked) weight += itemWeight;
      totalWeight += itemWeight;
    });
    this.setProgress(totalWeight ? Math.round(100 * weight / totalWeight) : 0);
  }

  /** ***** */
  /* SAVE */
  /** ***** */
  generateContentPatch(oldState, props, forDeletion = false) {
    const oldStateElementsById = {};
    const newStateElementsById = {};
    if (oldState) {
      let oldStateElement = oldState;
      if (props?.length) oldStateElement = _.pick(oldStateElement, props);
      oldStateElementsById[`id=${this.id}`] = oldStateElement;
    } // else creation
    if (! forDeletion) {
      let newStateElement = this.getAll();
      if (props?.length) newStateElement = _.pick(newStateElement, props);
      newStateElementsById[`id=${this.id}`] = newStateElement;
    } // else deletion

    return JsonPatchSrv.getElementsDiff(oldStateElementsById, newStateElementsById);
  }

  saveView(viewId, action = 'update') {
    return window.apiSrv.call(`views/${viewId}/elements`, action, this.getAll()).then((response) => {
      const savedElement = response?.data?.element;
      if (savedElement) this.set(savedElement);
    });
  }

  create() {
    return window.apiSrv.call(`plannings/${this.getPlanning().id}/elements`, 'store', this.getAll()).then((response) => {
      const savedElement = response?.data?.element;
      if (savedElement) this.set(savedElement);
    });
  }
}

/** ********************** */
/* BUSINESS CONFIG HELPERS */
/** ********************** */
function convertToBusinessTimeEquivalent(time) {
  const { businessHours, businessHoursRatio } = useUsersAccessRightStore().config;
  if (! businessHours) return time;
  const startOfDay = moment(time).startOf('day');
  const minutesInDay = time.diff(startOfDay, 'minutes');
  return startOfDay.add((Math.min(minutesInDay, businessHours.end) - Math.min(minutesInDay, businessHours.start)) * businessHoursRatio, 'minutes');
}
function convertFromBusinessTimeEquivalent(businessTime) {
  const { businessHours, businessHoursRatio } = useUsersAccessRightStore().config;
  if (! businessHours) return businessTime;
  const startOfDay = moment(businessTime).startOf('day');
  const minutesInDay = businessTime.diff(startOfDay, 'minutes');
  return moment(businessTime).startOf('day').minutes(businessHours.start).add(minutesInDay / businessHoursRatio, 'minutes');
}

export default PlanningElement;
